import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';

import { isApiError } from '@nucleus-commerce/api-client/transport';

export interface SessionModuleOptions<TMe> {
  /** Fetches /me. Must resolve to null when there is no session (see `nullOnUnauthorized`). */
  fetchMe: () => Promise<TMe | null>;
  logout: () => Promise<unknown>;
  queryKey?: readonly unknown[];
}

/** Wrap a /me call so a 401 resolves to null instead of rejecting. */
export async function nullOnUnauthorized<T>(promise: Promise<T>): Promise<T | null> {
  try {
    return await promise;
  } catch (error) {
    if (isApiError(error) && error.kind === 'unauthorized') return null;
    throw error;
  }
}

/**
 * The session is a TanStack Query, not a store: one source of truth, refetched on focus,
 * cleared on logout or on any 401 from the transport. Parameterised by surface so the tenant
 * admin and platform admin share the plumbing with different `Me` shapes.
 */
export function createSessionModule<TMe>({
  fetchMe,
  logout,
  queryKey = ['session', 'me'],
}: SessionModuleOptions<TMe>) {
  const meQueryOptions = queryOptions<TMe | null>({
    queryKey,
    queryFn: fetchMe,
    staleTime: 5 * 60_000,
    retry: false,
    meta: { silentError: true },
  });

  function useSession() {
    const query = useQuery(meQueryOptions);
    return {
      me: query.data ?? null,
      isAuthenticated: query.data != null,
      isPending: query.isPending,
      error: query.error,
      refetch: query.refetch,
    };
  }

  function useLogout() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: logout,
      meta: { silentError: true },
      onSettled: () => {
        queryClient.setQueryData(queryKey, null);
        queryClient.removeQueries({ predicate: (q) => q.queryKey !== queryKey });
      },
    });
  }

  /** For route `beforeLoad`: resolves the cached session or fetches it once. */
  function ensureSession(queryClient: QueryClient): Promise<TMe | null> {
    return queryClient.query({ ...meQueryOptions, staleTime: 'static' });
  }

  /** Called from the transport's onUnauthorized hook. */
  function clearSession(queryClient: QueryClient): void {
    queryClient.setQueryData(queryKey, null);
  }

  function setSession(queryClient: QueryClient, me: TMe | null): void {
    queryClient.setQueryData(queryKey, me);
  }

  /** After login: fetch /me fresh and cache it so route guards see the new session. */
  function refreshSession(queryClient: QueryClient): Promise<TMe | null> {
    return queryClient.query({ ...meQueryOptions, staleTime: 0 });
  }

  return {
    queryKey,
    meQueryOptions,
    useSession,
    useLogout,
    ensureSession,
    clearSession,
    setSession,
    refreshSession,
  };
}

export type SessionModule<TMe> = ReturnType<typeof createSessionModule<TMe>>;
